import { formatMoney, formatDate } from '../lib/format';
import {
  useTransactionList,
  TransactionListToolbar,
  Pager,
  ListStates,
  SortableTh,
  type StatusOption,
} from './TransactionList';
import {
  CustomerFilter,
  DateRangeFilter,
} from './TransactionFilters';
import { ViewHeader } from './DashboardLayout';
import { useConnection, stripeSubscriptionUrl, hubspotDealUrl } from './links';
import { ExtLink } from './ExtLink';
import type { SubscriptionChangeListEntry } from './types';

/**
 * Dashboard → Subscription Changes (M19 T2 + T3).
 *
 * Upgrade/downgrade history: one row per delta Deal (created when a
 * subscription's items change). The domain filter is `delta_type`
 * ('upgrade'/'downgrade') rather than a status. ΔMRR is signed (negative for
 * downgrades). The universal Customer filter resolves via the subscription
 * (delta deals have no direct customer column).
 *
 * Reference: subscription_delta_deals table (docs/cross-cutting/data-model.md).
 */

const TYPE_OPTIONS: StatusOption[] = [
  { value: 'upgrade', label: 'Upgrade', tone: 'success' },
  { value: 'downgrade', label: 'Downgrade', tone: 'danger' },
];

export function SubscriptionChanges() {
  const list = useTransactionList<SubscriptionChangeListEntry>(
    'admin/subscription-changes',
    'deltaType'
  );
  const {
    data,
    loading,
    error,
    refetch,
    status,
    setStatus,
    customer,
    setCustomer,
    dateRange,
    setDateRange,
    sort,
    dir,
    toggleSort,
    setOffset,
  } = list;

  return (
    <>
      <ViewHeader
        title="Subscription Changes"
        subtitle="Upgrades and downgrades — expansion and contraction history."
      />
      <TransactionListToolbar
        status={status}
        setStatus={setStatus}
        statusOptions={TYPE_OPTIONS}
        statusLabel="Type"
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <CustomerFilter
          selected={customer}
          selectedLabel={null}
          onSelect={(id) => {
            setCustomer(id);
            setOffset(0);
          }}
          onClear={() => setCustomer(null)}
        />
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
      </div>

      {(loading || error || (data && data.items.length === 0)) && (
        <ListStates
          loading={loading}
          error={error}
          empty={!!data && data.items.length === 0}
          onRetry={refetch}
        />
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl border border-ink-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Subscription</th>
                  <SortableTh
                    label="Type"
                    sortKey="type"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                  <SortableTh
                    label="Δ MRR"
                    sortKey="amount"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <th className="px-4 py-2 font-medium">HubSpot Deal</th>
                  <SortableTh
                    label="Created"
                    sortKey="created"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.items.map((c) => (
                  <ChangeRow key={c.id} change={c} />
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            total={data.total}
            offset={data.offset}
            pageSize={data.limit}
            setOffset={setOffset}
          />
        </>
      )}
    </>
  );
}

function ChangeRow({ change }: { change: SubscriptionChangeListEntry; }) {
  const { portalId, stripeAccountId } = useConnection();
  const stripeUrl = stripeSubscriptionUrl(
    stripeAccountId,
    change.stripeSubscriptionId
  );
  const hsUrl = hubspotDealUrl(portalId, change.hubspotDeltaDealId ?? '');
  const isUpgrade = change.deltaType === 'upgrade';
  return (
    <tr className="hover:bg-ink-50/50">
      <td className="px-4 py-2">
        {stripeUrl ? (
          <ExtLink
            href={stripeUrl}
            label={change.stripeSubscriptionId}
            title="View in Stripe"
          />
        ) : (
          <span className="text-xs text-ink-600">
            {change.stripeSubscriptionId}
          </span>
        )}
      </td>
      <td className="px-4 py-2">
        <span
          className={[
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
            isUpgrade
              ? 'bg-success-500/10 text-success-600'
              : 'bg-danger-500/10 text-danger-600',
          ].join(' ')}
        >
          {isUpgrade ? '↑ Upgrade' : '↓ Downgrade'}
        </span>
      </td>
      <td
        className={[
          'px-4 py-2 whitespace-nowrap text-right font-medium',
          isUpgrade ? 'text-success-600' : 'text-danger-600',
        ].join(' ')}
      >
        {change.deltaMrrMinor >= 0 ? '+' : ''}
        {formatMoney(change.deltaMrrMinor, 'USD')}
      </td>
      <td className="px-4 py-2">
        {change.hubspotDeltaDealId && hsUrl ? (
          <ExtLink href={hsUrl} label={change.hubspotDeltaDealId} title="View in HubSpot" />
        ) : (
          <span className="text-xs text-ink-400">—</span>
        )}
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-xs text-ink-500" title={formatDate(change.createdAt)}>
        {formatDate(change.createdAt)}
      </td>
    </tr>
  );
}
